import React, { useMemo } from 'react';
import ActivityBlock from './ActivityBlock';
import { sortActivitiesByPosition } from '../utils/activityUtils';

/**
 * ActivitySequenceRow - Renders a horizontal scrollable list of activity blocks.
 * 
 * @param {Object} props
 * @param {Array} props.activitySequence - Array of activity objects with { activity_code, activity_name, position }
 * @param {Object} props.colorPalette - Map of activity_code to color
 * @param {Object} props.activityCatalog - Map of activity_code to { code, name }
 * @param {boolean} props.wrap - When true, blocks wrap across lines instead of
 *   staying on a single horizontally-scrolling line (used in expanded rows).
 */
const ActivitySequenceRow = ({ activitySequence, colorPalette, activityCatalog, wrap = false }) => {
  // Sort activities by position
  const sortedActivities = useMemo(
    () => sortActivitiesByPosition(activitySequence),
    [activitySequence]
  );

  if (!sortedActivities || sortedActivities.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic py-1.5">
        No activities
      </div>
    );
  }

  return (
    <div
      className={`flex gap-2 py-1.5 pr-2 ${wrap ? 'flex-wrap' : ''}`}
      style={wrap ? undefined : { minWidth: 'max-content', whiteSpace: 'nowrap' }}
    >
      {sortedActivities.map((activity, index) => {
        const code = activity.activity_code || activity.code;
        const name = activityCatalog?.[code]?.name || activity.activity || activity.activity_name || activity.name || code;
        const color = colorPalette?.[code] || '#94a3b8';

        return (
          <ActivityBlock
            key={`${code}-${index}`}
            activityCode={code}
            activityName={name}
            color={color}
            index={index}
          />
        );
      })}
    </div>
  );
};

export default ActivitySequenceRow;
